import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useMyPoints, useMyRank, usePublicGamificationSettings } from '@/hooks';
import { Trophy, Flame, ChevronRight, Sparkles, Crown } from 'lucide-react';
import { useLocaleFormat } from '@/i18n/locale';

export function GamificationWidget() {
  const { t } = useTranslation('common');
  const { intlLocale } = useLocaleFormat();
  const { data: myPoints, isLoading } = useMyPoints();
  const { data: myRank } = useMyRank();
  const { data: publicSettings } = usePublicGamificationSettings();

  const settingsData = (publicSettings as any)?.data || publicSettings;
  if (!settingsData?.enabled) return null;

  const pointsData = (myPoints as any)?.data || myPoints;
  const rankData = (myRank as any)?.data || myRank;

  const totalPoints = pointsData?.totalPoints || 0;
  const currentLevel = pointsData?.level;
  const nextLevel = currentLevel?.nextLevel;
  const hasLevels = Boolean(currentLevel?.id);
  const streak = pointsData?.currentStreak || 0;
  const rank = rankData?.rank;
  const levelColor = currentLevel?.color || 'hsl(var(--primary))';

  const progressToNext = nextLevel
    ? Math.min(
        100,
        Math.round(
          ((totalPoints - (currentLevel?.minPoints || 0)) /
            ((nextLevel.minPoints || nextLevel.pointsNeeded || 1) -
              (currentLevel?.minPoints || 0))) *
            100,
        ),
      )
    : 100;

  if (isLoading) {
    return (
      <div className="space-y-3 px-3 pt-2 pb-3">
        <div className="h-2 w-full animate-pulse rounded-full bg-muted" />
        <div className="grid grid-cols-2 gap-2">
          <div className="h-12 animate-pulse rounded-lg bg-muted" />
          <div className="h-12 animate-pulse rounded-lg bg-muted" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 px-3 pt-2 pb-3">
      {nextLevel ? (
        <div>
          <div className="mb-1.5 flex items-baseline justify-between gap-2">
            <span className="truncate text-[11px] text-muted-foreground">
              {t('gamificationWidget.nextLevel')}{' '}
              <span
                className="font-semibold"
                style={{ color: nextLevel.color || levelColor }}
              >
                {nextLevel.name}
              </span>
            </span>
            <span
              className="shrink-0 font-mono text-[11px] font-bold tabular-nums"
              style={{ color: levelColor }}
            >
              {progressToNext}%
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${progressToNext}%`,
                background: `linear-gradient(90deg, ${levelColor}, ${levelColor}CC)`,
              }}
            />
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            <Trans
              t={t}
              i18nKey="gamificationWidget.xpToNext"
              values={{
                xp: Math.max(
                  0,
                  (nextLevel.minPoints ||
                    (currentLevel?.minPoints || 0) + nextLevel.pointsNeeded) -
                    totalPoints,
                ).toLocaleString(intlLocale),
              }}
              components={{
                strong: <span className="font-semibold text-foreground" />,
              }}
            />
          </p>
        </div>
      ) : (
        <div className="flex items-start gap-2 rounded-lg bg-muted/60 px-2.5 py-2">
          {hasLevels ? (
            <Crown
              className="mt-0.5 h-3.5 w-3.5 shrink-0"
              style={{ color: levelColor }}
            />
          ) : (
            <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-ink" />
          )}
          <p className="text-[11px] leading-snug text-muted-foreground">
            {hasLevels
              ? t('gamificationWidget.maxLevel')
              : t('gamificationWidget.earnXpHint')}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-border/50 px-2.5 py-2">
          <div className="flex items-center gap-1 text-[10px] tracking-wide text-muted-foreground uppercase">
            <Trophy className="h-3 w-3" />
            {t('gamificationWidget.position')}
          </div>
          <div className="mt-0.5 font-mono text-sm font-bold text-foreground tabular-nums">
            {rank ? `#${rank}` : '—'}
          </div>
        </div>
        <div className="rounded-lg border border-border/50 px-2.5 py-2">
          <div className="flex items-center gap-1 text-[10px] tracking-wide text-muted-foreground uppercase">
            <Flame className="h-3 w-3" />
            {t('gamificationWidget.streak')}
          </div>
          <div className="mt-0.5 text-sm font-bold text-foreground">
            {t('gamificationWidget.streakDays', { count: streak })}
          </div>
        </div>
      </div>

      <Link
        to="/ranking"
        className="group flex items-center justify-between rounded-lg px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary/40 hover:text-foreground"
      >
        {t('gamificationWidget.viewRanking')}
        <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </Link>
    </div>
  );
}
